import React from 'react';
import { X, AlertTriangle, CreditCard, ArrowRight } from 'lucide-react';

interface OutstandingBalanceModalProps {
    isOpen: boolean;
    onClose: () => void;
    balance: number;
    onPay: () => void;
}

export function OutstandingBalanceModal({ isOpen, onClose, balance, onPay }: OutstandingBalanceModalProps) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-500/10 rounded-lg">
                            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold text-foreground">Outstanding Balance</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-muted rounded-full transition-colors"
                    >
                        <X className="h-5 w-5 text-muted-foreground" />
                    </button>
                </div>

                <div className="p-8 text-center text-foreground">
                    <div className="mb-6">
                        <p className="text-sm text-muted-foreground mb-2">You have an unpaid balance of</p>
                        <div className="text-5xl font-black tracking-tight text-foreground">
                            ${balance.toFixed(2)}
                        </div>
                    </div>

                    <div className="bg-muted/50 rounded-xl p-4 mb-8 text-left border border-border">
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            To ensure continued access to premium features and the ability to switch or upgrade your plan, please settle your outstanding balance.
                        </p>
                    </div>

                    <div className="grid gap-3">
                        <button
                            onClick={onPay}
                            className="w-full py-4 px-6 bg-foreground text-background font-bold rounded-xl hover:bg-foreground/90 transition-all flex items-center justify-center gap-2 group shadow-lg"
                        >
                            <CreditCard className="h-5 w-5" />
                            Pay Outstanding Amount
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            onClick={onClose}
                            className="w-full py-4 px-6 bg-transparent text-muted-foreground font-semibold rounded-xl hover:bg-muted transition-colors"
                        >
                            Close & Pay Later
                        </button>
                    </div>
                </div>

                <div className="px-6 py-4 bg-muted/30 border-t border-border">
                    <p className="text-[10px] text-center text-muted-foreground uppercase tracking-wider font-bold">
                        Secure payment via Razorpay
                    </p>
                </div>
            </div>
        </div>
    );
}
